
import { useState } from 'react';

interface Review {
  id: string;
  title: string;
  content: string;
  verdict: string;
  author: string;
  category: string;
  genre: string;
  featured: boolean;
  published: boolean;
  bestseller: boolean;
  rating: number;
  cover_image: string;
  created_at: string;
  updated_at: string;
}

interface ReviewsFiltersProps {
  reviews: Review[];
  selectedCategory: string;
  selectedGenre: string;
  selectedRating: number;
  showBestsellers: boolean;
  onCategoryChange: (category: string) => void;
  onGenreChange: (genre: string) => void;
  onRatingChange: (rating: number) => void;
  onBestsellerToggle: (show: boolean) => void;
}

export default function ReviewsFilters({
  reviews,
  selectedCategory,
  selectedGenre,
  selectedRating,
  showBestsellers,
  onCategoryChange,
  onGenreChange,
  onRatingChange,
  onBestsellerToggle,
}: ReviewsFiltersProps) {
  const [localSelectedCategory, setLocalSelectedCategory] = useState('');
  const [localSelectedGenre, setLocalSelectedGenre] = useState('');
  const [localSelectedRating, setLocalSelectedRating] = useState('');
  const [selectedYear, setSelectedYear] = useState('');

  const categories = [
    'Fiction Review',
    'Non-Fiction Review',
    'Non Fiction',
    'Poetry Review',
    'Poetry',
    'Literary Analysis',
    'Biography',
    'Essays'
  ];

  const genres = [
    'Literary Fiction',
    'Historical Fiction',
    'Mystery',
    'Thriller',
    'Crime Fiction',
    'Suspense',
    'Psychological Fiction',
    'Drama',
    'Adventure',
    'Satire',
    'Humor',
    'Family Saga',
    'Coming-of-Age',
    'Women\'s Fiction',
    'Urban Fiction',
    'Political Fiction',
    'War Fiction',
    'Dystopian Fiction',
    'Utopian Fiction',
    'Experimental Fiction',
    'Science Fiction',
    'Fantasy',
    'Horror',
    'Contemporary Romance',
    'Historical Romance',
    'Romantic Comedy',
    'LGBTQ+ Romance',
    'Cozy Mystery',
    'True Crime',
    'LGBTQ+ Mystery',
    'Religion/Spirituality',
    'Self Help',
    'Non-Fiction',
    'Memoir',
    'History'
  ];

  const ratings = [5, 4, 3, 2, 1];
  const years = ['2024', '2023', '2022', '2021', '2020'];

  const clearFilters = () => {
    setLocalSelectedCategory('');
    setLocalSelectedGenre('');
    setLocalSelectedRating('');
    setSelectedYear('');
    onCategoryChange('');
    onGenreChange('');
    onRatingChange(0);
    onBestsellerToggle(false);
  };

  const hasActiveFilters = localSelectedCategory || localSelectedGenre || localSelectedRating || selectedYear || showBestsellers;

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-8">
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <i className="ri-filter-3-line mr-2"></i>
        Filter Reviews
      </h3>

      {/* Bestseller Toggle */}
      <div className="mb-6">
        <label className="flex items-center cursor-pointer">
          <input
            type="checkbox"
            checked={showBestsellers}
            onChange={(e) => onBestsellerToggle(e.target.checked)}
            className="h-4 w-4 text-amber-600 focus:ring-amber-500 border-gray-300 rounded cursor-pointer"
          />
          <span className="ml-2 text-sm font-medium text-gray-700 flex items-center">
            <i className="ri-award-fill text-amber-500 mr-1"></i>
            Show only bestsellers
          </span>
        </label>
      </div>

      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-bold text-gray-900" style={{ fontFamily: '"Libre Baskerville", serif' }}>
          Filter Reviews
        </h3>
        {hasActiveFilters && (
          <button
            onClick={clearFilters}
            className="text-sm text-red-600 hover:text-red-700 cursor-pointer"
          >
            Clear All
          </button>
        )}
      </div>

      {/* Category Filter */}
      <div className="mb-6">
        <h4 className="font-semibold text-gray-900 mb-3">Category</h4>
        <div className="space-y-2">
          {categories.map((category) => (
            <label key={category} className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="category"
                value={category}
                checked={localSelectedCategory === category}
                onChange={(e) => {
                  setLocalSelectedCategory(e.target.value);
                  onCategoryChange(e.target.value);
                }}
                className="sr-only"
              />
              <div className={`w-4 h-4 border-2 rounded-full mr-3 ${
                localSelectedCategory === category 
                  ? 'bg-red-600 border-red-600' 
                  : 'border-gray-300'
              }`}>
                {localSelectedCategory === category && (
                  <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>
                )}
              </div>
              <span className="text-sm text-gray-700">{category}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Genre Filter */}
      <div className="mb-6">
        <h4 className="font-semibold text-gray-900 mb-3">Genre</h4>
        <div className="space-y-2 max-h-48 overflow-y-auto">
          {genres.map((genre) => (
            <label key={genre} className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="genre"
                value={genre}
                checked={localSelectedGenre === genre}
                onChange={(e) => {
                  setLocalSelectedGenre(e.target.value);
                  onGenreChange(e.target.value);
                }}
                className="sr-only"
              />
              <div className={`w-4 h-4 border-2 rounded-full mr-3 ${
                localSelectedGenre === genre 
                  ? 'bg-red-600 border-red-600' 
                  : 'border-gray-300'
              }`}>
                {localSelectedGenre === genre && (
                  <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>
                )}
              </div>
              <span className="text-sm text-gray-700">{genre}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Rating Filter */}
      <div className="mb-6">
        <h4 className="font-semibold text-gray-900 mb-3">Minimum Rating</h4>
        <div className="space-y-2">
          {ratings.map((rating) => (
            <label key={rating} className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="rating"
                value={rating.toString()}
                checked={localSelectedRating === rating.toString()}
                onChange={(e) => {
                  setLocalSelectedRating(e.target.value);
                  onRatingChange(Number(e.target.value));
                }}
                className="sr-only"
              />
              <div className={`w-4 h-4 border-2 rounded-full mr-3 ${
                localSelectedRating === rating.toString()
                  ? 'bg-red-600 border-red-600' 
                  : 'border-gray-300'
              }`}>
                {localSelectedRating === rating.toString() && (
                  <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>
                )}
              </div>
              <div className="flex items-center space-x-1">
                {Array.from({ length: rating }, (_, i) => (
                  <i key={i} className="ri-star-fill text-yellow-400 text-sm"></i>
                ))}
                <span className="text-sm text-gray-700 ml-2">& up</span>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Year Filter */}
      <div className="mb-6">
        <h4 className="font-semibold text-gray-900 mb-3">Publication Year</h4>
        <div className="space-y-2">
          {years.map((year) => (
            <label key={year} className="flex items-center cursor-pointer">
              <input
                type="radio"
                name="year"
                value={year}
                checked={selectedYear === year}
                onChange={(e) => setSelectedYear(e.target.value)}
                className="sr-only"
              />
              <div className={`w-4 h-4 border-2 rounded-full mr-3 ${
                selectedYear === year 
                  ? 'bg-red-600 border-red-600' 
                  : 'border-gray-300'
              }`}>
                {selectedYear === year && (
                  <div className="w-2 h-2 bg-white rounded-full mx-auto mt-0.5"></div>
                )}
              </div>
              <span className="text-sm text-gray-700">{year}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Search */}
      <div>
        <h4 className="font-semibold text-gray-900 mb-3">Search</h4>
        <div className="relative">
          <input
            type="text"
            placeholder="Search reviews..."
            className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm"
          />
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center">
            <i className="ri-search-line text-gray-400 text-sm"></i>
          </div>
        </div>
      </div>
    </div>
  );
}
